import React, { useMemo, type FC } from 'react';

import { TimeSlot } from '../../../state/infiniteCalendarSeries';
import type { InfiniteCalendarSeriesProps } from '../../InfiniteCalendarSeries/InfiniteCalendarSeries';
import { SESSION_SIZE } from '../types';
import DiscountFooter from './DiscountFooter/DiscountFooter';
import SimpleTimeSlotList from './SimpleTimeSlotList/SimpleTimeSlotList';
import styles from './TimeSlotContainer.module.scss';
import { TimeSlotTabHeader } from './TimeSlotTabHeader/TimeSlotTabHeader';
import { useTab } from './useTab';
import { sortSessionsByStartTime } from './utils';

type TimeSlotContainerProps = {
    timeSlotData: TimeSlot[];
    onTimeSelect: (sessionId: string, sessionLabel: string) => void;
    selectedSessionId: string | undefined;
    showFooter: boolean;
    currency?: string;
} & Pick<
    InfiniteCalendarSeriesProps,
    'isAppRebrandActive' | 'timezone' | 'locale' | 'currency'
>;

export const TimeSlotContainer: FC<TimeSlotContainerProps> = ({
    timeSlotData,
    timezone,
    locale,
    onTimeSelect,
    isAppRebrandActive,
    selectedSessionId,
    showFooter,
    currency,
}) => {
    const sessions = useMemo(
        () => sortSessionsByStartTime(timeSlotData),
        [timeSlotData],
    );

    const {
        overallDiscount,
        activeTab,
        sessionsByDurationAndTimeOfDay,
        setActiveTab,
    } = useTab(sessions, timezone, selectedSessionId, currency);

    return (
        <div
            className={styles.timeSlotContainer}
            data-testid="time-slot-container"
        >
            {sessions.length < SESSION_SIZE ? (
                <SimpleTimeSlotList
                    onSessionClick={onTimeSelect}
                    sessions={sessions}
                    timezone={timezone}
                    locale={locale}
                    isAppRebrandActive={isAppRebrandActive}
                    currency={currency}
                />
            ) : (
                <TimeSlotTabHeader
                    onSessionClick={onTimeSelect}
                    activeTab={activeTab}
                    sessionsByDurationAndTimeOfDay={
                        sessionsByDurationAndTimeOfDay
                    }
                    isAppRebrandActive={isAppRebrandActive}
                    setActiveTab={setActiveTab}
                    timezone={timezone}
                    locale={locale}
                    currency={currency}
                />
            )}
            {overallDiscount && showFooter ? (
                <DiscountFooter
                    discount={overallDiscount}
                    isAppRebrandActive={isAppRebrandActive}
                />
            ) : null}
        </div>
    );
};
